/**
 * DemoRequestCTA — shared full-width dark-section CTA for marketing pages.
 */

'use client';

import React from 'react';
import { MotionSection } from '@/components/marketing/motion-section';
import { MktgSection } from '@/components/marketing/mktg-section';
import { MktgHeading } from '@/components/marketing/mktg-heading';
import { MktgBodyText } from '@/components/marketing/mktg-body-text';
import { MktgButton } from '@/components/marketing/mktg-button';
import { YellowHighlight } from '@/components/marketing/yellow-highlight';
import { TrustBadges } from '@/components/marketing/trust-badges';
import { StatusDot } from '@/components/marketing/status-dot';

interface DemoRequestCTAProps {
  title?: string;
  description?: string;
  buttonLabel?: string;
  className?: string;
}

export function DemoRequestCTA({
  title,
  description = 'Join leading international schools using TEX to simplify counselor workflows and connect students to real opportunities.',
  buttonLabel = 'Request a Demo',
  className
}: DemoRequestCTAProps) {
  return (
    <>
      <MktgSection
        background='dark'
        className={className}
        containerStyle={{ maxWidth: '800px', textAlign: 'center' }}
        aria-label='Demo request CTA'
      >
        <MotionSection>
          <MktgHeading as='h2' size='lg' color='white' className='mb-5'>
            {title ?? (
              <>
                Ready to{' '}
                <YellowHighlight variant='draw'>transform</YellowHighlight>{' '}
                career readiness at your school?
              </>
            )}
          </MktgHeading>
          <MktgBodyText
            size='lg'
            color='white-muted'
            className='mx-auto mb-10'
            style={{ maxWidth: '600px' }}
          >
            {description}
          </MktgBodyText>
          <MktgButton variant='primary' size='lg' href='/request-demo'>
            {buttonLabel}
          </MktgButton>

          {/* Compliance chips at the point of conversion */}
          <TrustBadges variant='dark' className='mt-8' />
        </MotionSection>
      </MktgSection>

      {/* Subtle divider separating the CTA from the footer */}
      <div
        style={{
          height: '1px',
          background:
            'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 20%, rgba(255,255,255,0.12) 80%, transparent 100%)',
          backgroundColor: 'var(--mktg-dark-deep)'
        }}
      />
    </>
  );
}
